import { clampMs, trackAuditEvent } from "@/api/auditEvents";
import { useMemo, useRef } from "react";
import { dockStatus } from "./auditDockModel";
import type { BackgroundAuditState } from "./useBackgroundCodeRuleAudit";

export type AuditBarTrigger = "audit-half" | "notes-half" | "drag";

export function useAuditBarEvents(caseId: string, audit: BackgroundAuditState | undefined) {
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;
  const auditRef = useRef(audit);
  auditRef.current = audit;
  const openedAtRef = useRef<number | null>(null);

  return useMemo(() => {
    const markOpen = () => {
      openedAtRef.current = performance.now();
    };
    return {
      markOpen,
      restartIfOpen: (isOpen: boolean) => {
        openedAtRef.current = isOpen ? performance.now() : null;
      },
      opened: (trigger: AuditBarTrigger) => {
        markOpen();
        const current = auditRef.current;
        if (!current) return;
        const { tone, highCount, mediumCount } = dockStatus(current);
        trackAuditEvent({
          type: "audit_bar_opened",
          encounterId: caseIdRef.current,
          trigger,
          tone,
          highCount,
          mediumCount,
        });
      },
      closed: (trigger: AuditBarTrigger) => {
        const openedAt = openedAtRef.current;
        openedAtRef.current = null;
        if (!auditRef.current) return;
        trackAuditEvent({
          type: "audit_bar_closed",
          encounterId: caseIdRef.current,
          trigger,
          openMs: openedAt === null ? 0 : clampMs(performance.now() - openedAt),
        });
      },
      peekShown: (newHighCount: number) => {
        markOpen();
        const current = auditRef.current;
        if (!current) return;
        trackAuditEvent({
          type: "audit_peek_shown",
          encounterId: caseIdRef.current,
          highCount: dockStatus(current).highCount,
          newHighCount,
        });
      },
    };
  }, []);
}
